import classNames from 'classnames';
import { type ComponentProps, type MouseEventHandler, memo } from 'react';

import { Tooltip } from '@air/primitive-tooltip';
import { cn } from '@air/utils-tailwind';

import { CustomField, type CustomFieldProps } from '~/components/CustomField';
import { type GetFieldsToRenderAndOverflowCountProps, getFieldsToRenderAndOverflowCount } from '~/utils/CustomFields';

export type CustomFieldListWithOverflowProps = Pick<GetFieldsToRenderAndOverflowCountProps, 'customFields'> &
  Pick<ComponentProps<'div'>, 'className'> & {
    allowedWidth: number;
    hasDarkBackground?: boolean;
    shouldShowTooltip?: boolean;
    onClick?: MouseEventHandler<HTMLButtonElement>;
    'data-testid'?: string;
    size?: CustomFieldProps['size'];
  };

export const CustomFieldListWithOverflow = memo(
  ({
    allowedWidth,
    customFields,
    className,
    hasDarkBackground = false,
    shouldShowTooltip = false,
    onClick,
    size = 'medium',
    'data-testid': dataTestId,
  }: CustomFieldListWithOverflowProps) => {
    const { fieldsToRender, hiddenFieldsCount } = getFieldsToRenderAndOverflowCount({
      allowedWidth,
      customFields,
      widthPerChar: 10,
    });

    if (!fieldsToRender.length) return null;

    return (
      <button
        className={cn(
          'flex w-full items-center justify-start gap-1 rounded-4',
          onClick ? 'cursor-pointer hover:bg-grey-5' : 'cursor-default hover:bg-transparent',
          className,
        )}
        disabled={!onClick}
        onClick={onClick}
        data-testid={dataTestId}
        onMouseDown={(event) => event.preventDefault()}
        type="button"
      >
        <div className="flex items-center gap-1">
          {fieldsToRender.map((field) => {
            if (field.value) {
              if (shouldShowTooltip) {
                return (
                  <Tooltip label={field.fieldName} key={field.id}>
                    <CustomField
                      data-testid="CUSTOM_FIELD"
                      data-testid-name={field.fieldName}
                      data-testid-value={field.value}
                      field={field}
                      size={size}
                    />
                  </Tooltip>
                );
              }

              return (
                <CustomField
                  data-testid="CUSTOM_FIELD"
                  data-testid-name={field.fieldName}
                  data-testid-value={field.value}
                  field={field}
                  key={field.id}
                  size={size}
                />
              );
            }

            return null;
          })}
        </div>

        {hiddenFieldsCount > 0 && (
          <div className="shrink-0">
            <div className={classNames('font-medium text-12', hasDarkBackground ? 'text-white' : 'text-black')}>
              +{hiddenFieldsCount}
            </div>
          </div>
        )}
      </button>
    );
  },
);

CustomFieldListWithOverflow.displayName = 'CustomFieldListWithOverflow';
